import {
  useState, useEffect, useMemo, useRef,
  Dispatch, SetStateAction, useCallback,
} from 'react';
import { GlobalValuesTypes, SetFormValues } from '@components/common/Forms';
import { dequal } from 'dequal';
import { useSwrJobGenerateRequirements } from '@hooks/useSwrApi/endpoints/job-requirements/useSwrJobGenerateRequirements';
import { MirrorData, SetMirrorData } from '@type/job-add';
import { MAX_ENABLED_INTERVIEW_QUESTIONS } from 'src/constants';

/** Props */
interface UseJobRequirementQuestions {
  getFieldValue: (name: string) => GlobalValuesTypes,
  initialRequirementQuestions?: ApiSchema<'InterviewQuestion'>[];
  updateFormElements?: () => void;
  setRequirementQuestionList?: Dispatch<SetStateAction<ApiSchema<'InterviewQuestion'>[] | undefined>>;
  inputName: string;
  shouldFetchQuestions?: boolean,
  reqMirrorData?: Pick<MirrorData, 'title' | 'description'>,
  setMirrorData?: SetMirrorData,
  labelDescriptionClass?: string,
  locale: Locale,
  setFormValues: SetFormValues,
}


/**
 * @description Job Requirement Questions hook.
 * - `getFieldValue` - get fields value function from useBjForm
 * - `initialRequirementQuestions` - initial requirement questions
 * - `updateFormElements` - update form elements function from useBjForm
 * - `setRequirementQuestionList` - set requirement question list function
 * - `inputName` - name of the input
 * - `shouldFetchQuestions` - whether to fetch the questions
 * - `reqMirrorData`: The required form mirror data.
 * - `setMirrorData` - function to update the mirror form state
 * - `labelDescriptionClass` - additional classes for the label description
 */
export const useJobRequirementQuestions = (props: UseJobRequirementQuestions) => {
  // Destructure props
  const {
    getFieldValue,
    initialRequirementQuestions,
    updateFormElements,
    setRequirementQuestionList,
    inputName,
    shouldFetchQuestions,
    reqMirrorData,
    locale,
  } = props;

  // SWR job requirement questions generation
  // *************************
  const {
    data: jobGeneratedRequirements,
  } = useSwrJobGenerateRequirements({
    locale,
    payload: {
      title: reqMirrorData?.title ?? '',
      ...(reqMirrorData?.description ? { description: reqMirrorData.description } : {}),
    },
    shouldFetch: shouldFetchQuestions || (!initialRequirementQuestions || initialRequirementQuestions.length === 0),
  });


  const formattedQuestions = useMemo(() => {
    if (!jobGeneratedRequirements?.questions) return [];

    // Filter out duplicates and empty strings from AI response
    const uniqueQuestions = Array.from(new Set(jobGeneratedRequirements.questions.filter((q: string) => q.trim() !== '')));

    return uniqueQuestions.map((q: string) => ({
      question: q,
      enabled: true,
      readOnly: false,
    }));
  }, [jobGeneratedRequirements]);


  // STATES
  // **************************
  // The requirement questions
  const prevInitialRequirementQuestions = useRef<ApiSchema<'InterviewQuestion'>[]>(initialRequirementQuestions ?? []);

  /**
   * Helper to limit enabled questions to MAX_ENABLED_INTERVIEW_QUESTIONS
   */
  const limitEnabledQuestions = (questions: ApiSchema<'InterviewQuestion'>[]) => {
    let enabledCount = 0;
    return questions.filter((q) => {
      if (q.enabled) {
        enabledCount += 1;
        return enabledCount <= MAX_ENABLED_INTERVIEW_QUESTIONS;
      }
      return true;
    });
  };

  const [requirementQuestions, setRequirementQuestions] = useState<ApiSchema<'InterviewQuestion'>[]>(
    limitEnabledQuestions(initialRequirementQuestions ?? formattedQuestions ?? []),
  );
  // Reset requirement questions if initialRequirementQuestions change
  useEffect(() => {
    if (!dequal(prevInitialRequirementQuestions.current, initialRequirementQuestions)) {
      prevInitialRequirementQuestions.current = initialRequirementQuestions ?? [];
      setRequirementQuestions(limitEnabledQuestions(initialRequirementQuestions ?? []));
    }
  }, [initialRequirementQuestions]);

  // Requirement record options
  const getRequirementRecordOptions = useCallback(() => requirementQuestions.map((question, index) => ({
    id: `${inputName}_${index}`,
    defaultValue: question.question,
    valueAsArray: true,
    removeFromSubmit: true,
  })), [requirementQuestions, inputName]);
  const requirementRecordOptions = getRequirementRecordOptions();


  // FUNCTIONS
  // **************************
  // Add a question.
  const addQuestion = () => {
    setRequirementQuestions((prev) => [...prev, { question: '', enabled: true, readOnly: false }]);
  };

  // Delete a question.
  const deleteQuestion = (questionIndex: number) => {
    setRequirementQuestions((prev) => {
      const questions = [...prev];
      questions.splice(questionIndex, 1);
      return questions;
    });
  };

  // Save questions
  const saveQuestions = () => {
    const values = getFieldValue(inputName) as string[];
    // Map the requirementQuestions state and update the text
    setRequirementQuestions((prev) => {
      const newQuestions = prev.map((question, index) => {
        const text = values?.[index] ?? '';
        return { ...question, question: text };
      });

      if (!dequal(prev, newQuestions)) {
        return newQuestions;
      }
      return prev;
    });
  };


  // SET MAIN STATE THAT WILL BE PASSED TO FORM
  // **************************
  useEffect(() => {
    if (setRequirementQuestionList && requirementQuestions.length > 0) {
      updateFormElements?.();
      // filter out empty questions
      const filteredQuestions = requirementQuestions.filter((question) => question.question !== '');
      setRequirementQuestionList((prev) => {
        if (filteredQuestions.length === 0) return undefined;
        // Don't update if the questions are the same
        if (!dequal(prev, filteredQuestions)) return filteredQuestions;
        return prev;
      });
    }
  }, [requirementQuestions, setRequirementQuestionList, updateFormElements]);

  // Update requirementQuestions whenever formattedQuestions changes (e.g., after fetch)
  useEffect(() => {
    if (formattedQuestions.length > 0) {
      setRequirementQuestions((prev) => {
        // Only overwrite if the current list is empty or only has one empty question
        const isEmpty = prev.length === 0 || (prev.length === 1 && prev[0]?.question?.trim() === '');
        if (isEmpty) {
          return limitEnabledQuestions(formattedQuestions);
        }
        return prev;
      });
    }
  }, [formattedQuestions]);


  // Return
  // **************************
  return {
    requirementRecordOptions,
    requirementQuestions,
    setRequirementQuestions,
    addQuestion,
    deleteQuestion,
    saveQuestions,
  };
};
